/**
 * RcAttributionBanner — surfaces RC call rows in the last 24h that
 * landed in `activity_log` with no `user_id` and no `agent_id`.
 *
 * Calls /api/reports/rc-attribution-health. Renders only when the
 * endpoint returns `status === "warning"` or `"critical"` — staying
 * silent on `"ok"` so the page isn't cluttered.
 *
 * Severity bands:
 *   • ok       — < 5% orphans  → render nothing
 *   • warning  — 5–15%         → amber banner
 *   • critical — > 15%         → red banner
 *
 * Manager-only. The endpoint already returns 403 for non-managers,
 * so we just swallow that quietly.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, X, ChevronDown, ChevronUp } from "lucide-react";
import { API, useAuth } from "@/App";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function RcAttributionBanner({ hoursBack = 24 }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.get(`${API}/reports/rc-attribution-health?hours_back=${hoursBack}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      if (!cancelled) setData(res.data);
    }).catch(() => { /* not a manager / no RC = silent */ });
    return () => { cancelled = true; };
  }, [token, hoursBack]);

  if (dismissed || !data) return null;
  if (data.status === "ok" || (data.total_calls || 0) === 0) return null;

  const critical = data.status === "critical";
  const bg     = critical ? "#FEE2E2" : "#FEF3C7";
  const border = critical ? "#F87171" : "#FBBF24";
  const fg     = critical ? "#991B1B" : "#854F0B";
  const iconFg = critical ? "#DC2626" : "#D97706";

  return (
    <div
      data-testid="rc-attribution-banner"
      data-status={data.status}
      style={{
        padding: "12px 16px", borderRadius: 10, marginBottom: 16,
        background: bg, border: `1px solid ${border}`, color: fg,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <AlertTriangle style={{ width: 18, height: 18, color: iconFg, flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>
            {critical ? "Critical" : "Warning"} · {data.orphans} of {data.total_calls} RC calls in the last {data.window_hours}h ({data.orphan_pct}%) are untracked
          </div>
          <div style={{ fontSize: 12, opacity: 0.85, lineHeight: 1.45 }}>
            These calls aren't credited to any rep. The most likely cause is a rep dialling RingCentral from outside the CRM (mobile app, desk phone with no OAuth) or a revoked OAuth token. Ask reps to reconnect RingCentral in <strong>Settings → Integrations</strong>.
          </div>
          {data.recent_orphans?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <button
                data-testid="rc-attribution-banner-toggle"
                onClick={() => setExpanded((v) => !v)}
                style={{
                  background: "rgba(255,255,255,0.5)", border: `0.5px solid ${border}`,
                  borderRadius: 6, padding: "3px 8px", fontSize: 11, fontWeight: 600,
                  color: fg, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
                }}
              >
                {expanded ? <ChevronUp style={{ width: 11, height: 11 }} /> : <ChevronDown style={{ width: 11, height: 11 }} />}
                {expanded ? "Hide" : "Show"} recent untracked calls ({data.recent_orphans.length})
              </button>
              {expanded && (
                <div data-testid="rc-attribution-banner-samples" style={{ marginTop: 8, fontSize: 11, fontFamily: "var(--font-mono, monospace)", background: "rgba(255,255,255,0.6)", padding: 8, borderRadius: 6 }}>
                  {data.recent_orphans.map((o, i) => (
                    <div key={i} style={{ padding: "3px 0", borderBottom: i < data.recent_orphans.length - 1 ? "0.5px dashed rgba(0,0,0,0.08)" : "none" }}>
                      <strong style={{ textTransform: "uppercase" }}>{o.direction || "?"}</strong>
                      {" · "}from <strong>{o.from_number || "—"}</strong>
                      {" → "}to <strong>{o.to_number || "—"}</strong>
                      {" · "}<span style={{ opacity: 0.65 }}>{fmtTime(o.at || o.created_at)}{o.outcome ? ` · ${o.outcome}` : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <button
          data-testid="rc-attribution-banner-dismiss"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss attribution warning"
          style={{ background: "none", border: "none", padding: 4, cursor: "pointer", color: fg, opacity: 0.7, flexShrink: 0 }}
        >
          <X style={{ width: 14, height: 14 }} />
        </button>
      </div>
    </div>
  );
}
